import React, { FunctionComponent, MouseEvent, useMemo, useState } from "react";

import { MouseButton } from "@multiweb/types/react";
import { LOC_CANCEL, LOC_CHOOSE_ACTIVITIES_TO_ADD, LOC_NO_EXISTING_ACTIVITIES_MESSAGE } from "../../../../locKeys";
import { UiWorkflow } from "../../../../UiWorkflow";
import { Activity } from "../../../../workflowModel";
import { BasicDialog } from "../../BasicDialog";
import { BasicDialogItem } from "../../components";

export type ExistingActivitiesDialogComponentProps = {
    readonly selectedSetId: string;
    readonly uiWorkflow: UiWorkflow;
    readonly onAddClick: (activitiesToAdd: Activity[]) => void;
    readonly onCancelClick: () => void;
    readonly addText: string;
};

export const ExistingActivitiesDialogComponent: FunctionComponent<ExistingActivitiesDialogComponentProps> = ({
    selectedSetId,
    uiWorkflow,
    onAddClick,
    onCancelClick,
    addText,
}) => {
    const [selectedActivitiesIndexes, setSelectedActivitiesIndexes] = useState<readonly number[]>([]);

    const existingActivities = useMemo(() => uiWorkflow.workflow.getExistingActivities(selectedSetId).sort((a, b) => a.name.localeCompare(b.name)), [selectedSetId, uiWorkflow.workflow]);

    const handleAddClick = () => {
        onAddClick(selectedActivitiesIndexes.map(selectedActivityIndex => existingActivities[selectedActivityIndex]));
    };

    const handleItemClick = (e: MouseEvent, activityIndex: number) => {
        if (e.ctrlKey && e.button === MouseButton.leftButton) {
            if (selectedActivitiesIndexes.includes(activityIndex))
                setSelectedActivitiesIndexes(selectedActivitiesIndexes.filter(activity => activity !== activityIndex));
            else
                setSelectedActivitiesIndexes(activitiesIndexes => [...activitiesIndexes, activityIndex]);
        } else
            setSelectedActivitiesIndexes([activityIndex]);
    };

    return (
        <BasicDialog
            cancelTextLocKey={LOC_CANCEL}
            headerLocKey={LOC_CHOOSE_ACTIVITIES_TO_ADD}
            noItemsLocKey={LOC_NO_EXISTING_ACTIVITIES_MESSAGE}
            buttonDisabled={selectedActivitiesIndexes.length === 0}
            onCloseChange={onCancelClick}
            onConfirmClick={handleAddClick}
            confirmationButtonText={`${addText} (${selectedActivitiesIndexes.length})`}>
            {existingActivities.map(({ name, id }, index) =>
                <BasicDialogItem
                    key={id}
                    isSelected={selectedActivitiesIndexes.includes(index)}
                    onItemClick={(e) => handleItemClick(e, index)}
                    {...{ name }} />,
            )}
        </BasicDialog>
    );
};
